import type { Comic } from '@tanko/shared'
import type { Library } from '../db/schema'
import { libraryForComic } from './library-sources'

/**
 * 從雲端開啟一本作品時，頁面要從哪裡來。
 *
 * 閱讀狀態已經與來源脫鉤——不論頁面來自本機還是 R2，進度與書籤都寫到雲端目錄項。因此
 * 來源只剩下一個問題：位元組從哪裡拿比較划算。本機有同一本就讀本機，不重複下載。
 */
export type ReadingSourceChoice =
  | { source: 'local'; comic: Comic; library: Library }
  /** mismatch 有值代表本機確實有這一本，但頁數對不上，因此刻意不用它。 */
  | { source: 'cloud'; mismatch?: { local: number; cloud: number } }

/**
 * 四個條件全部成立才用本機那一份：
 *
 * - 本機有綁到同一個雲端識別碼的作品
 * - 它已經完成索引（頁數與每頁尺寸才是確定的）
 * - 它的來源還在，而且這次工作階段已經授權
 * - 頁數與雲端記錄相同
 *
 * 頁數不符不自動替換也不猜：雲端那一份是備份完成、與雲端頁碼一致的那一份，用它一定
 * 讀得出正確的內容。差異回報給呼叫端顯示，讓使用者看得到而不是被默默換掉。
 */
export function chooseReadingSource(
  cloud: { id: string; pageCount: number },
  comics: readonly Comic[],
  libraries: readonly Library[],
  isAuthorized: (libraryId: string) => boolean,
): ReadingSourceChoice {
  const comic = comics.find(item => item.cloudComicId === cloud.id)
  if (!comic || !comic.indexed) return { source: 'cloud' }
  const library = libraryForComic(libraries, comic.libraryId)
  if (!library || !isAuthorized(library.id)) return { source: 'cloud' }
  if (comic.pageCount !== cloud.pageCount) {
    return { source: 'cloud', mismatch: { local: comic.pageCount, cloud: cloud.pageCount } }
  }
  return { source: 'local', comic, library }
}

/** 頁數不符時給使用者看的說明。兩個數字都講出來，差異才看得見。 */
export const mismatchNotice = (mismatch: { local: number; cloud: number }): string =>
  `本機這一份有 ${mismatch.local} 頁，雲端記錄的是 ${mismatch.cloud} 頁，因此這次從雲端讀取。`
